(() => {
  if (!customElements.get('search-bar')) {
    class SearchBar extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[name="q"]');
        this.form = this.querySelector('form');
        this.region = this.querySelector('[data-search-bar-region]');
        this.clearButton = this.querySelector('[data-search-bar-clear]');
        this.cache = new Map();
        this.timer = null;
        this.controller = null;

        this.onInput = this.onInput.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onClick = this.onClick.bind(this);

        this.input.addEventListener('input', this.onInput);
        this.form.addEventListener('submit', this.onSubmit);
        this.addEventListener('keydown', this.onKeydown);
        this.addEventListener('click', this.onClick);
        this.syncClear();
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        if (this.controller) this.controller.abort();
      }

      term() {
        return this.input.value.trim();
      }

      focusInput() {
        this.input.focus();
      }

      syncClear() {
        this.clearButton.hidden = this.input.value.length === 0;
      }

      onInput() {
        this.syncClear();
        clearTimeout(this.timer);
        const term = this.term();
        if (term.length < 2) {
          if (this.controller) this.controller.abort();
          this.setLoading(false);
          this.close();
          return;
        }
        this.timer = setTimeout(() => this.search(term), 150);
      }

      onSubmit(event) {
        if (!this.term()) {
          event.preventDefault();
          this.input.focus();
        }
      }

      onClick(event) {
        if (event.target.closest('[data-search-bar-clear]')) {
          event.preventDefault();
          this.input.value = '';
          this.syncClear();
          this.close();
          this.input.focus();
        }
      }

      options() {
        return Array.from(this.region.querySelectorAll('[data-search-bar-option]'));
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          if (!this.region.hidden) {
            event.preventDefault();
            event.stopPropagation();
            this.close();
            this.input.focus();
          }
          return;
        }

        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const options = this.options();
        if (this.region.hidden || options.length === 0) return;

        event.preventDefault();
        const index = options.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') {
          options[index < 0 ? 0 : Math.min(index + 1, options.length - 1)].focus();
        } else if (index <= 0) {
          this.input.focus();
        } else {
          options[index - 1].focus();
        }
      }

      setLoading(loading) {
        this.classList.toggle('is-loading', loading);
      }

      async search(term) {
        if (this.cache.has(term)) {
          this.render(this.cache.get(term));
          return;
        }

        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        this.setLoading(true);

        const params = new URLSearchParams({
          q: term,
          section_id: this.dataset.section,
          'resources[type]': 'product',
          'resources[limit]': this.dataset.limit || '8',
          'resources[options][prefix]': 'last',
          'resources[options][unavailable_products]': 'last',
        });

        try {
          const response = await fetch(`${this.dataset.url}?${params}`, {
            signal: this.controller.signal,
          });
          if (!response.ok) throw new Error(`Search failed: ${response.status}`);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const results = doc.querySelector('[data-search-bar-results]');
          const markup = results ? results.outerHTML : '';
          this.cache.set(term, markup);

          if (this.term() === term) this.render(markup);
          this.setLoading(false);
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.setLoading(false);
          this.close();
        }
      }

      render(markup) {
        if (!markup) {
          this.close();
          return;
        }
        this.region.innerHTML = markup;
        this.region.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
      }

      close() {
        this.region.hidden = true;
        this.region.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
      }
    }

    customElements.define('search-bar', SearchBar);
  }

  if (window.themeHeaderSearchBound) return;
  window.themeHeaderSearchBound = true;

  let opener = null;
  const dialog = () => document.querySelector('[data-header-search]');

  const closeDialog = () => {
    const el = dialog();
    if (el && el.open) el.close();
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-header-search-open]');
    const el = dialog();

    if (trigger && el && typeof el.showModal === 'function') {
      event.preventDefault();
      opener = trigger;
      if (!el.open) el.showModal();
      document.documentElement.classList.add('header-search-open');
      const bar = el.querySelector('search-bar');
      requestAnimationFrame(() => bar && bar.focusInput && bar.focusInput());
      return;
    }

    if (!el || !el.open) return;
    if (event.target.closest('[data-header-search-close]') || event.target === el) {
      closeDialog();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const el = dialog();
    if (el && el.open) {
      event.preventDefault();
      closeDialog();
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      if (!event.target.matches || !event.target.matches('[data-header-search]')) return;
      document.documentElement.classList.remove('header-search-open');
      if (opener && opener.isConnected && opener.offsetParent !== null) opener.focus();
      opener = null;
    },
    true,
  );
})();
